import { FC, PropsWithChildren } from 'react';
import {
    Block,
    BLOCKS,
    helpers,
    Inline,
    INLINES
} from '@contentful/rich-text-types';
import HTMLWrapperEntry from './HTMLWrapperEntry/HTMLWrapperEntry';
import ImageWrapperEntry from './ImageWrapperEntry/ImageWrapperEntry';
import TableEntry from './TableEntry/TableEntry';
import ButtonEntry from './ButtonEntry/ButtonEntry';
import { FAQComponent } from './FAQs';
import { ReactComponentEntry } from './ReactComponentEntry/ReactComponentEntry';
import { isValidNode } from '../../types';
import { inlineButtonWrapper } from './EmbeddedEntry.styles.css';
import { Box } from '@zoopla-eng/zds-components/box';

type ContentType =
    | 'htmlWrapper'
    | 'basicTable'
    | 'imageWrapper'
    | 'button'
    | 'faq'
    | 'reactComponent';

type EntryType = BLOCKS.EMBEDDED_ENTRY | INLINES.EMBEDDED_ENTRY;

const SUPPORTED_CONTENT_TYPES: Record<EntryType, ContentType[]> = {
    // Block supports all content types
    [BLOCKS.EMBEDDED_ENTRY]: [
        'htmlWrapper',
        'basicTable',
        'imageWrapper',
        'button',
        'faq',
        'reactComponent'
    ],
    [INLINES.EMBEDDED_ENTRY]: ['button']
};

const isSupportedContentType = (
    nodeType: BLOCKS | INLINES,
    contentType: string
): contentType is ContentType => {
    if (
        nodeType !== BLOCKS.EMBEDDED_ENTRY &&
        nodeType !== INLINES.EMBEDDED_ENTRY
    ) {
        return false;
    }

    return !!SUPPORTED_CONTENT_TYPES[nodeType].find(
        (supportedType) => contentType === supportedType
    );
};

const ButtonWrapper: FC<PropsWithChildren<{ isBlock: boolean }>> = ({
    isBlock,
    children
}) => {
    return isBlock ? (
        <Box>{children}</Box>
    ) : (
        <span className={inlineButtonWrapper}>{children}</span>
    );
};

type EmbeddedEntryProps = {
    node: Inline | Block;
};

const EmbeddedEntry: FC<EmbeddedEntryProps> = ({ node }) => {
    if (!isValidNode(node)) {
        return (
            <div>
                <p>Invalid content type.</p>
            </div>
        );
    }

    const { sys, fields } = node.data.target;

    const contentType = sys.contentType.sys.id;

    if (!isSupportedContentType(node.nodeType, contentType)) {
        return (
            <div>
                <p>
                    Unsupported content type. Please select a valid content
                    type.
                </p>
            </div>
        );
    }

    // Before nodeData was typed as a valid NodeData, fields was any rather than
    // Record<string, any> which satisfied the type for faqs. This has been
    // changed now, but FAQComponent has not. In future, this should be
    // reworked to remove the need for this any
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const faqs: any = fields;

    switch (contentType) {
        case 'htmlWrapper':
            return <HTMLWrapperEntry node={node} />;

        case 'basicTable':
            return <TableEntry node={node} />;

        case 'imageWrapper':
            return <ImageWrapperEntry node={node} />;

        case 'button':
            return (
                <ButtonWrapper isBlock={helpers.isBlock(node)}>
                    <ButtonEntry
                        buttonUrl={fields.buttonUrl}
                        category={fields.category}
                        buttonLabel={fields.buttonLabel}
                        eventTracking={fields.eventTracking ?? undefined}
                    />
                </ButtonWrapper>
            );

        case 'faq':
            return <FAQComponent faqs={faqs} />;

        case 'reactComponent':
            return <ReactComponentEntry node={node} />;
    }
};

export default EmbeddedEntry;
